/**
 * useSSEValidation — connects the SSE stream to the Zustand validation store.
 *
 * Usage:
 *   const { generateLessonPlan, isRunning } = useSSEValidation();
 *   await generateLessonPlan({ lesson_id, version, cefr_level, topic, duration_min });
 */

import { useCallback, useRef } from 'react';
import { aiSSEService } from '../services/aiSSEService';
import { useValidationStore } from '../store/validationStore';
import type {
  GenerateLessonPlanData,
  GenerateSlidesData,
  RefineLessonPlanData,
  RefineStageData,
  SSECompleteData,
  SSEEvent,
} from '../types';

interface SSESubscription {
  abort: () => void;
}

export interface UseSSEValidationReturn {
  isRunning: boolean;
  generateLessonPlan: (data: GenerateLessonPlanData) => Promise<SSECompleteData | null>;
  refineLessonPlan: (data: RefineLessonPlanData) => Promise<SSECompleteData | null>;
  generateSlides: (data: GenerateSlidesData) => Promise<SSECompleteData | null>;
  refineStage: (data: RefineStageData) => Promise<SSECompleteData | null>;
  abort: () => void;
}

/**
 * Wraps aiSSEService with:
 * - Automatic store dispatch for every SSE event
 * - Drawer auto-open on generation start
 * - Promise that resolves with the complete event payload when the stream ends
 * - Abort support to cancel in-flight streams
 */
export function useSSEValidation(): UseSSEValidationReturn {
  const { dispatch, reset, openDrawer, status } = useValidationStore();
  const subscriptionRef = useRef<SSESubscription | null>(null);

  const isRunning = status === 'running';

  /**
   * Runs a generation call and returns a promise that resolves when the stream
   * completes (with the `complete` event payload) or null on error/abort.
   */
  const run = useCallback(
    async (
      startStream: (
        callbacks: { onEvent: (e: SSEEvent) => void; onComplete: () => void; onError: (err: Error) => void }
      ) => Promise<SSESubscription>
    ): Promise<SSECompleteData | null> => {
      reset();
      openDrawer();

      return new Promise<SSECompleteData | null>((resolve) => {
        let completePayload: SSECompleteData | null = null;

        const onEvent = (event: SSEEvent) => {
          dispatch(event);
          if (event.type === 'complete') {
            completePayload = event.data;
          }
        };

        const onComplete = () => {
          subscriptionRef.current = null;
          resolve(completePayload);
        };

        const onError = (_err: Error) => {
          subscriptionRef.current = null;
          resolve(null);
        };

        startStream({ onEvent, onComplete, onError }).then((sub) => {
          subscriptionRef.current = sub;
        });
      });
    },
    [dispatch, openDrawer, reset]
  );

  const generateLessonPlan = useCallback(
    (data: GenerateLessonPlanData) =>
      run((callbacks) => aiSSEService.generateLessonPlan(data, callbacks)),
    [run]
  );

  const refineLessonPlan = useCallback(
    (data: RefineLessonPlanData) =>
      run((callbacks) => aiSSEService.refineLessonPlan(data, callbacks)),
    [run]
  );

  const generateSlides = useCallback(
    (data: GenerateSlidesData) =>
      run((callbacks) => aiSSEService.generateSlides(data, callbacks)),
    [run]
  );

  const refineStage = useCallback(
    (data: RefineStageData) =>
      run((callbacks) => aiSSEService.refineStage(data, callbacks)),
    [run]
  );

  const abort = useCallback(() => {
    subscriptionRef.current?.abort();
    subscriptionRef.current = null;
  }, []);

  return { isRunning, generateLessonPlan, refineLessonPlan, generateSlides, refineStage, abort };
}
